import React, { useState, useEffect } from 'react';
import { Button, TextInput, StatusBanner, ScreenHeader, Spinner, SuccessTick, LinkButton } from './ui';
import { IconAlert, IconShield, IconBolt, IconCopy, IconCheck, IconGeneratorMailback } from './icons';

interface FetchModeProps {
  prefill?: { pan?: string; email?: string };
  onSubmit: (pan: string, email: string) => Promise<void>;
  /**
   * Called when the user is ready to move on to the upload step
   * (i.e. dismisses the "we've sent you an email" confirmation screen).
   */
  onContinueToUpload: () => void;
  /**
   * Optional jump to the inbound-email flow on the success screen. When
   * provided (i.e. the embedder has `enableInboundEmail` on), the
   * confirmation screen surfaces this as the primary CTA — the user just
   * asked a third party to mail them a PDF, and inbound email is exactly
   * the tool to receive it without download/re-upload.
   */
  onJumpToInbound?: (email: string) => void;
  onBack: () => void;
  isLoading: boolean;
  /**
   * Password the registrar will encrypt the mailed CAS with. Surfaced
   * verbatim on the success screen so users opening the PDF inside
   * their email client (where re-uploading isn't an option) can copy
   * it without coming back to the widget.
   */
  casPassword?: string;
}

export const FetchMode: React.FC<FetchModeProps> = ({
  prefill,
  onSubmit,
  onContinueToUpload,
  onJumpToInbound,
  onBack,
  isLoading,
  casPassword,
}) => {
  const [pan, setPan] = useState(prefill?.pan || '');
  const [email, setEmail] = useState(prefill?.email || '');
  const [error, setError] = useState('');
  const [invalidField, setInvalidField] = useState<'pan' | 'email' | null>(null);
  const [sent, setSent] = useState<{ email: string } | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleCopyPassword = async () => {
    if (!casPassword) return;
    try {
      await navigator.clipboard.writeText(casPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) — user can
      // still select the visible value manually.
    }
  };

  useEffect(() => {
    if (prefill?.pan) setPan(prefill.pan);
    if (prefill?.email) setEmail(prefill.email);
  }, [prefill]);

  const validatePan = (v: string) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v);
  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInvalidField(null);
    const upperPan = pan.toUpperCase();
    if (!validatePan(upperPan)) {
      setError('That PAN doesn\'t look right — format should be ABCDE1234F.');
      setInvalidField('pan');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      setInvalidField('email');
      return;
    }
    try {
      await onSubmit(upperPan, email);
      setSent({ email });
    } catch (err: any) {
      setError(err.message || 'Couldn\'t send the request. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '56px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        aria-live="polite"
      >
        <Spinner size={44} />
        <h3
          style={{
            margin: '20px 0 6px',
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--pc-color-text)',
          }}
        >
          Requesting your mutual fund CAS…
        </h3>
        <p
          style={{
            color: 'var(--pc-color-text-muted)',
            fontSize: 13,
            margin: 0,
          }}
        >
          This usually takes a few seconds
        </p>
      </div>
    );
  }

  if (sent) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <SuccessTick size={56} />
        <h3
          style={{
            margin: '14px 0 8px',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--pc-color-text)',
          }}
        >
          Request sent
        </h3>
        <p
          style={{
            color: 'var(--pc-color-text-muted)',
            fontSize: 14,
            margin: '0 auto 16px',
            lineHeight: 1.5,
            maxWidth: 340,
          }}
        >
          Your mutual fund CAS will be emailed to{' '}
          <strong style={{ color: 'var(--pc-color-text)' }}>{sent.email}</strong>{' '}
          — usually under 2 minutes. Check your spam folder if it's not there.
        </p>

        {/* Password the registrar will encrypt the PDF with. Shown
            here because users often open the attachment directly in
            their email client — they need the password without
            coming back to the widget. */}
        {casPassword && (
          <div
            style={{
              backgroundColor: 'var(--pc-color-surface-subtle)',
              border: '1px solid var(--pc-color-border)',
              borderRadius: 'var(--pc-radius-md)',
              padding: '12px 14px',
              marginBottom: 16,
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--pc-color-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 6,
              }}
            >
              PDF password
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <code
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, monospace',
                  color: 'var(--pc-color-text)',
                  background: 'var(--pc-color-surface)',
                  border: '1px solid var(--pc-color-border)',
                  borderRadius: 'var(--pc-radius-sm)',
                  padding: '6px 10px',
                  userSelect: 'all',
                  wordBreak: 'break-all',
                }}
              >
                {casPassword}
              </code>
              <Button
                variant="secondary"
                onClick={handleCopyPassword}
                aria-label="Copy password"
                iconLeft={
                  passwordCopied ? (
                    <IconCheck size={14} />
                  ) : (
                    <IconCopy size={14} />
                  )
                }
              >
                {passwordCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--pc-color-text-faded)',
                marginTop: 6,
                lineHeight: 1.4,
              }}
            >
              Use this to open the PDF in your email — we'll fill it in
              automatically when you upload here.
            </div>
          </div>
        )}

        {/* Cross-flow nudge — the user just asked a third party to email
            them a PDF; inbound email lets us grab it the moment it arrives.
            Only shown when the embedder has enableInboundEmail on. */}
        {onJumpToInbound ? (
          <>
            <div
              style={{
                backgroundColor: 'var(--pc-color-primary-soft)',
                border: '1px solid var(--pc-color-primary-soft-border)',
                borderRadius: 'var(--pc-radius-md)',
                padding: '14px 16px',
                marginBottom: 14,
                textAlign: 'left',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  color: 'var(--pc-color-primary)',
                  marginTop: 2,
                }}
              >
                <IconBolt size={18} />
              </span>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--pc-color-text)',
                    marginBottom: 2,
                  }}
                >
                  Skip the download — auto-import when it arrives
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--pc-color-text-muted)',
                    lineHeight: 1.45,
                  }}
                >
                  Forward the CAS email to a unique address we'll give you,
                  and your portfolio imports automatically.
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              fullWidth
              onClick={() => onJumpToInbound(sent.email)}
            >
              Auto-import via forwarding
            </Button>
            <div style={{ marginTop: 10 }}>
              <LinkButton type="button" onClick={onContinueToUpload}>
                I'll upload the PDF myself
              </LinkButton>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <StatusBanner tone="warning" icon={<IconAlert size={16} />}>
                <strong>Check your spam folder</strong> — these emails
                sometimes land there.
              </StatusBanner>
            </div>
            <p
              style={{
                color: 'var(--pc-color-text-muted)',
                fontSize: 13,
                margin: '0 0 16px',
                lineHeight: 1.5,
              }}
            >
              When the email arrives, drop the PDF here. The password is
              already filled in.
            </p>
            <Button variant="primary" fullWidth onClick={onContinueToUpload}>
              OK, take me to upload
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <ScreenHeader
        icon={<IconGeneratorMailback size={22} />}
        title="Email me a fresh CAS"
        description="KFintech/CAMS mails you one statement covering all your mutual fund holdings."
      />

      <StatusBanner tone="info">
        Use the email registered with your PAN — KFintech only sends the
        CAS there (CAMS data is included in the same statement).
      </StatusBanner>

      <form onSubmit={handleSubmit} style={{ marginTop: 18 }}>
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--pc-color-text)',
              marginBottom: 6,
            }}
          >
            PAN number
          </label>
          <TextInput
            value={pan}
            invalid={invalidField === 'pan'}
            onChange={(e) =>
              // Strip whitespace from paste; auto-uppercase as the user types.
              setPan(e.target.value.replace(/\s+/g, '').toUpperCase())
            }
            placeholder="ABCDE1234F"
            maxLength={10}
            style={{ textTransform: 'uppercase' }}
            autoComplete="off"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--pc-color-text)',
              marginBottom: 6,
            }}
          >
            Email address
          </label>
          <TextInput
            type="email"
            value={email}
            invalid={invalidField === 'email'}
            onChange={(e) => setEmail(e.target.value.trim())}
            placeholder="you@example.com"
            autoComplete="email"
          />
          <div
            style={{
              fontSize: 11,
              color: 'var(--pc-color-text-faded)',
              marginTop: 4,
            }}
          >
            Your statement will be sent here
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: 14 }}>
            <StatusBanner tone="error" icon={<IconAlert size={16} />}>
              {error}
            </StatusBanner>
          </div>
        )}

        <Button variant="primary" fullWidth type="submit">
          Send my statement
        </Button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginTop: 14,
            fontSize: 11,
            color: 'var(--pc-color-text-faded)',
          }}
        >
          <IconShield size={12} />
          <span>We don't store your PAN or email — it's only used for this request.</span>
        </div>

        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--pc-color-text-muted)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textDecoration: 'underline',
            }}
          >
            I already have my file
          </button>
        </div>
      </form>
    </div>
  );
};
